import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type AppImPlatform = 'feishu' | 'wecom' | 'dingtalk'
export type AppImMode = 'open' | 'whitelist' | 'disabled'
export type AppImStatus = 'draft' | 'active' | 'disabled'

export interface AppImPlatformSummary {
  agent_platform_snapshot: string | null
  configured: boolean
  display_name: string
  im_platform: AppImPlatform
  integration_id: string | null
  status: AppImStatus | null
  supported: boolean
  verification_status: AppImVerificationStatus | null
}

export type AppImVerificationStatus = 'unverified' | 'verifying' | 'verified' | 'failed' | 'stale'

export interface AppImPlatformConfig {
  aibotid?: string
  agent_id?: string
  app_id?: string
  app_secret_masked?: string
  corp_id?: string
  has_app_secret?: boolean
  has_encoding_aes_key?: boolean
  has_encrypt_key?: boolean
  has_token?: boolean
  has_verification_token?: boolean
}

export interface AppImVerification {
  credential_version: number
  last_error_code?: string | null
  last_verified_at?: string | null
  status: AppImVerificationStatus
  verified_credential_version?: number | null
}

export type AppImIdentityReadinessStatus = 'blocked' | 'ready' | 'unknown'

export interface AppImIdentityReadiness {
  action_required: string[]
  checked_at?: string
  items: Record<
    string,
    {
      action_required?: string
      status: AppImIdentityReadinessStatus
    }
  >
  status: AppImIdentityReadinessStatus
}

export interface AppImReadinessSummary {
  action_required?: string[]
  checked_at?: string | null
  status: AppImIdentityReadinessStatus
}

export interface AppImCallbackReadiness extends AppImReadinessSummary {
  items: {
    code: string
    message: string
    severity: 'blocking' | 'info' | 'warning'
    status: 'failed' | 'passed' | 'unknown'
  }[]
}

export type AppImWecomIdentityProfileReadiness = Omit<AppImIdentityReadiness, 'checked_at'>

export interface AppImWecomIdentityProfileOption {
  display_name: string
  id: string
  readiness: AppImWecomIdentityProfileReadiness
  status: 'active' | 'disabled'
}

export type AppImWecomIdentityProfileDetail = AppImWecomIdentityProfileOption

export interface AppImIntegrationDetail {
  agent_platform_snapshot: string | null
  app_id: string
  configured: boolean
  generated_secrets?: {
    encrypt_key?: string
    verification_token?: string
  }
  im_platform: AppImPlatform
  integration_id: string | null
  identity_profile?: AppImWecomIdentityProfileDetail | null
  platform_config: AppImPlatformConfig
  status: AppImStatus
  strategy_config: {
    group_message_mode: string
    group_mode: AppImMode
    group_whitelist: string[]
    p2p_mode: AppImMode
    p2p_whitelist: string[]
  }
  verification: AppImVerification
  wecom_identity_profile_id?: string | null
}

export interface AppImUpsertBody {
  platform_config: {
    agent_id?: string
    app_id?: string
    app_secret?: string
    corp_id?: string
    encoding_aes_key?: string
    token?: string
  }
  strategy_config: {
    group_mode: AppImMode
    group_whitelist: string[]
    p2p_mode: AppImMode
    p2p_whitelist: string[]
  }
  wecom_identity_profile_id?: string
}

export interface AppImSetupManifest {
  callback_readiness?: AppImCallbackReadiness
  callback_url: string
  oauth_callback_url?: string | null
  credential_presence: Record<string, boolean>
  credential_version: number
  diagnostics: {
    code: string
    message: string
    severity: 'blocking' | 'info' | 'warning'
    status: 'failed' | 'passed' | 'unknown'
  }[]
  integration_id: string
  identity_readiness?: AppImIdentityReadiness
  identity_profile?: {
    display_name: string
    id: string
    readiness: AppImWecomIdentityProfileReadiness
  } | null
  platform: AppImPlatform
  readiness: 'blocked' | 'ready' | 'unknown'
  verification_status: AppImVerificationStatus
  verified_credential_version?: number | null
}

export interface AppImValidationResult {
  errors: {
    field: string
    message: string
  }[]
  identity_readiness?: AppImIdentityReadiness
  passed: boolean
  warnings: {
    field: string
    message: string
  }[]
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appImPath(appId: string, suffix = '') {
  return `/admin/app/${encodeURIComponent(appId)}/im-integrations${suffix}`
}

export async function listAdminAppImPlatforms(appId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{
      items: AppImPlatformSummary[]
    }>
  >(appImPath(appId))

  return response.data.items
}

export async function listAdminAppWecomIdentityProfiles(appId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{ items: AppImWecomIdentityProfileOption[] }>
  >(appImPath(appId, '/wecom/identity-profiles'))

  return response.data.items
}

export async function getAdminAppImIntegrationDetail(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<SuccessEnvelope<AppImIntegrationDetail>>(
    appImPath(appId, `/${encodeURIComponent(platform)}`),
  )

  return response.data
}

export async function getAdminAppImSetupManifest(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<SuccessEnvelope<AppImSetupManifest>>(
    appImPath(appId, `/${encodeURIComponent(platform)}/setup-manifest`),
  )

  return response.data
}

export async function getAdminAppImVerification(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<SuccessEnvelope<AppImVerification>>(
    appImPath(appId, `/${encodeURIComponent(platform)}/verification`),
  )

  return response.data
}

export async function upsertAdminAppImIntegration(
  appId: string,
  platform: AppImPlatform,
  body: AppImUpsertBody,
) {
  const response = await apiClient.request<SuccessEnvelope<AppImIntegrationDetail>>(
    appImPath(appId, `/${encodeURIComponent(platform)}`),
    {
      body: JSON.stringify(body),
      method: 'PUT',
    },
  )

  return response.data
}

export async function validateAdminAppImIntegration(
  appId: string,
  platform: AppImPlatform,
  body: AppImUpsertBody,
) {
  const response = await apiClient.request<SuccessEnvelope<AppImValidationResult>>(
    appImPath(appId, `/${encodeURIComponent(platform)}/validate`),
    {
      body: JSON.stringify(body),
      method: 'POST',
    },
  )

  return response.data
}

export async function enableAdminAppImIntegration(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<
    SuccessEnvelope<{
      im_platform: AppImPlatform
      status: AppImStatus
    }>
  >(appImPath(appId, `/${encodeURIComponent(platform)}/enable`), {
    method: 'POST',
  })

  return response.data
}

export async function disableAdminAppImIntegration(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<
    SuccessEnvelope<{
      im_platform: AppImPlatform
      status: AppImStatus
    }>
  >(appImPath(appId, `/${encodeURIComponent(platform)}/disable`), {
    method: 'POST',
  })

  return response.data
}

export async function deleteAdminAppImIntegration(appId: string, platform: AppImPlatform) {
  const response = await apiClient.request<MessageEnvelope>(
    appImPath(appId, `/${encodeURIComponent(platform)}`),
    {
      method: 'DELETE',
    },
  )

  return response.message
}
